import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { awsConfig } from "./config";

/**
 * Cognito session handling, shaped like the Supabase auth surface the app was
 * built against: a session object, an auth-state subscription, and
 * sign-in / sign-up / sign-out.
 *
 * The SDK keeps tokens in localStorage and refreshes them with the refresh
 * token, so `getSession` is the single place that hands out a fresh JWT.
 */

export interface AuthUser {
  id: string;
  email: string | null;
}

export interface AuthSession {
  accessToken: string;
  idToken: string;
  expiresAt: number;
  user: AuthUser;
}

export type AuthChangeEvent = "SIGNED_IN" | "SIGNED_OUT" | "TOKEN_REFRESHED" | "USER_UPDATED";

const userPool = new CognitoUserPool({
  UserPoolId: awsConfig.userPoolId,
  ClientId: awsConfig.userPoolClientId,
});

type Listener = (event: AuthChangeEvent, session: AuthSession | null) => void;
const listeners = new Set<Listener>();

function emit(event: AuthChangeEvent, session: AuthSession | null): void {
  for (const listener of listeners) {
    try {
      listener(event, session);
    } catch (error) {
      console.error("Auth listener failed", error);
    }
  }
}

function toSession(session: CognitoUserSession): AuthSession {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload() as { sub?: string; email?: string };
  return {
    // The API Gateway authorizer is configured against the ID token, which is
    // what carries the user's email and sub claims.
    accessToken: idToken.getJwtToken(),
    idToken: idToken.getJwtToken(),
    expiresAt: idToken.getExpiration() * 1000,
    user: { id: payload.sub ?? "", email: payload.email ?? null },
  };
}

let inFlight: Promise<AuthSession | null> | null = null;

/** Current session, refreshed transparently when the token has expired. */
export function getSession(): Promise<AuthSession | null> {
  if (inFlight) return inFlight;

  inFlight = new Promise<AuthSession | null>((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve(null);

    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) {
        if (error) console.warn("Could not restore the session", error.message);
        return resolve(null);
      }
      resolve(toSession(session));
    });
  }).finally(() => {
    inFlight = null;
  });

  return inFlight;
}

export async function getAccessToken(): Promise<string | null> {
  const session = await getSession();
  return session?.accessToken ?? null;
}

export async function getUser(): Promise<AuthUser | null> {
  const session = await getSession();
  return session?.user ?? null;
}

export function onAuthStateChange(listener: Listener): { unsubscribe: () => void } {
  listeners.add(listener);
  return { unsubscribe: () => listeners.delete(listener) };
}

export function signIn(email: string, password: string): Promise<AuthSession> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  const details = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => {
        const mapped = toSession(session);
        emit("SIGNED_IN", mapped);
        resolve(mapped);
      },
      onFailure: (error: Error) => reject(new Error(friendlyMessage(error))),
      newPasswordRequired: () =>
        reject(
          new Error(
            "This account needs a new password. Ask an administrator to reset it, then sign in again.",
          ),
        ),
    });
  });
}

export interface SignUpResult {
  /** True when Cognito emailed a confirmation code. */
  confirmationRequired: boolean;
}

export function signUp(email: string, password: string, fullName: string): Promise<SignUpResult> {
  const attributes = [
    new CognitoUserAttribute({ Name: "email", Value: email }),
    new CognitoUserAttribute({ Name: "name", Value: fullName }),
  ];

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, attributes, [], (error, result) => {
      if (error) return reject(new Error(friendlyMessage(error)));
      resolve({ confirmationRequired: result?.userConfirmed === false });
    });
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code.trim(), true, (error) => {
      if (error) return reject(new Error(friendlyMessage(error as Error)));
      resolve();
    });
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((error) => {
      if (error) return reject(new Error(friendlyMessage(error)));
      resolve();
    });
  });
}

/** Emails a reset code to the address, if an account exists for it. */
export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (error: Error) => {
        // Don't reveal whether the address has an account.
        if ((error as Error & { name?: string }).name === "UserNotFoundException") return resolve();
        reject(new Error(friendlyMessage(error)));
      },
    });
  });
}

export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(),
      onFailure: (error: Error) => reject(new Error(friendlyMessage(error))),
    });
  });
}

export async function signOut(): Promise<void> {
  const user = userPool.getCurrentUser();
  if (user) {
    await new Promise<void>((resolve) => {
      // Revokes the refresh token server-side as well as clearing local storage.
      user.signOut(() => resolve());
    });
  }
  emit("SIGNED_OUT", null);
}

/** Cognito's own wording is terse; these are the cases users actually hit. */
function friendlyMessage(error: Error & { name?: string; code?: string }): string {
  const code = error.name || error.code;
  switch (code) {
    case "NotAuthorizedException":
      return "Incorrect email or password";
    case "UserNotFoundException":
      return "Incorrect email or password";
    case "UserNotConfirmedException":
      return "Confirm your email address first — check your inbox for the code";
    case "UsernameExistsException":
      return "An account with that email already exists";
    case "InvalidPasswordException":
      return "Password must be at least 8 characters with upper and lower case letters and a number";
    case "CodeMismatchException":
      return "That confirmation code is not right";
    case "ExpiredCodeException":
      return "That confirmation code has expired — request a new one";
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts — wait a moment and try again";
    default:
      return error.message || "Authentication failed";
  }
}
